import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { useParams, useNavigate, useSearchParams, Link } from 'react-router-dom';
import { Sidebar } from '../../components/Sidebar';
import { Button } from '../../components/ui/button';
import {
  ArrowLeft, Loader2, Crown, Instagram, Youtube, MapPin, ExternalLink,
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const fmtNum = (n) => {
  if (n == null || n === '' || Number.isNaN(Number(n))) return '—';
  const v = Number(n);
  if (v >= 1000000) return `${(v / 1000000).toFixed(1)}M`;
  if (v >= 1000) return `${(v / 1000).toFixed(1)}K`;
  return v.toLocaleString();
};
const fmtInr = (n) => (n == null || n === '' ? '—' : `₹${Number(n).toLocaleString('en-IN')}`);
const fmtPct = (n) => (n == null || n === '' ? '—' : `${Number(n).toFixed(2)}%`);

// Determine the "winner" index for a given metric.
// direction: 'max' = highest is best, 'min' = lowest is best.
const winnerIndex = (values, direction) => {
  const nums = values.map((v) => (v == null || v === '' || Number.isNaN(Number(v)) ? null : Number(v)));
  // Need at least 2 numeric values for a meaningful winner
  const real = nums.filter((v) => v != null);
  if (real.length < 2) return -1;
  let bestIdx = -1;
  let bestVal = direction === 'max' ? -Infinity : Infinity;
  nums.forEach((v, i) => {
    if (v == null) return;
    if (direction === 'max' && v > bestVal) { bestVal = v; bestIdx = i; }
    if (direction === 'min' && v < bestVal) { bestVal = v; bestIdx = i; }
  });
  // If all are equal, no highlight
  if (nums.every((v) => v === bestVal)) return -1;
  return bestIdx;
};

export const BrandShortlistCompare = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const selectedIds = (params.get('ids') || '').split(',').map((s) => s.trim()).filter(Boolean);

  const [shortlist, setShortlist] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await axios.get(`${API_URL}/api/shortlists/${id}`, { withCredentials: true });
        if (!cancelled) setShortlist(data);
      } catch (err) {
        if (!cancelled) {
          toast.error(err.response?.data?.detail || 'Failed to load shortlist');
          navigate(`/brand/shortlists/${id}`);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [id, navigate]);

  const creators = useMemo(() => {
    if (!shortlist) return [];
    const idSet = new Set(selectedIds);
    return (shortlist.creators || []).filter((c) => idSet.has(c.id));
  }, [shortlist, selectedIds]);

  // Indexes of winners (per the four highlighted metrics) — used for green border + crown
  const winners = useMemo(() => {
    if (creators.length < 2) return {};
    return {
      followers: winnerIndex(creators.map((c) => c.instagram_followers), 'max'),
      engagement: winnerIndex(creators.map((c) => c.instagram_engagement), 'max'),
      reelPrice: winnerIndex(creators.map((c) => c.pricing_reel), 'min'),
      brandSafety: winnerIndex(creators.map((c) => c.brand_safety_score), 'max'),
    };
  }, [creators]);

  if (loading) {
    return (
      <div className="flex min-h-screen">
        <Sidebar />
        <div className="flex-1 flex items-center justify-center bg-white dark:bg-[#0A0B0D]">
          <Loader2 className="animate-spin text-[#FF451A]" size={32} />
        </div>
      </div>
    );
  }

  if (!shortlist) return null;

  if (creators.length < 2) {
    return (
      <div className="flex min-h-screen">
        <Sidebar />
        <div className="flex-1 p-8 bg-white dark:bg-[#0A0B0D]">
          <div className="max-w-4xl mx-auto">
            <button
              onClick={() => navigate(`/brand/shortlists/${id}`)}
              className="text-sm text-[#6B6B6B] dark:text-zinc-400 hover:text-white flex items-center gap-1 mb-6"
              data-testid="compare-back-button"
            >
              <ArrowLeft size={14} /> Back to shortlist
            </button>
            <div className="bg-white dark:bg-[#0A0B0D] border border-black/[0.08] dark:border-white/5 rounded-2xl p-12 text-center" data-testid="compare-empty-state">
              <h2 className="text-xl font-semibold text-[#0F0F0F] dark:text-white mb-2">Pick at least 2 creators to compare</h2>
              <p className="text-sm text-[#6B6B6B] dark:text-zinc-400 mb-4">Open the shortlist, hit "Compare", and tick 2–4 rows.</p>
              <Link
                to={`/brand/shortlists/${id}`}
                className="inline-flex items-center gap-2 bg-[#FF451A] text-[#0F0F0F] dark:text-white rounded-full px-5 py-2.5 font-medium hover:bg-[#E63E17]"
              >
                Back to shortlist
              </Link>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Metric rows — declared in order. Each row knows: label, render(creator) -> ReactNode, and (optionally) the winner key.
  const rows = [
    { label: 'Followers',           winnerKey: 'followers',   render: (c) => <span className="inline-flex items-center gap-1"><Instagram size={11} className="text-[#E1306C]" /> {fmtNum(c.instagram_followers)}</span> },
    { label: 'Engagement',          winnerKey: 'engagement',  render: (c) => fmtPct(c.instagram_engagement) },
    { label: 'Avg Views',           render: (c) => fmtNum(c.avg_views) },
    { label: 'Reel Price',          winnerKey: 'reelPrice',   render: (c) => fmtInr(c.pricing_reel) },
    { label: 'Story Price',         render: (c) => fmtInr(c.pricing_story) },
    { label: 'YouTube Subscribers', render: (c) => <span className="inline-flex items-center gap-1"><Youtube size={11} className="text-[#FF0000]" /> {fmtNum(c.youtube_subscribers)}</span> },
    { label: 'Brand Safety',        winnerKey: 'brandSafety', render: (c) => c.brand_safety_score != null ? `${c.brand_safety_score}` : '—' },
    { label: 'Fake Followers',      render: (c) => c.fake_follower_score != null ? `${c.fake_follower_score}%` : '—' },
    { label: 'Quality Grade',       render: (c) => c.quality_grade ? <span className="px-1.5 py-0.5 rounded-md text-[11px] bg-white/5 border border-black/[0.10] dark:border-white/10 font-mono">{c.quality_grade}</span> : '—' },
    { label: 'Exclusivity',         render: (c) => c.exclusivity_type === 'exclusive' ? 'Managed' : 'Marketplace' },
    {
      label: 'Categories',
      render: (c) => (c.categories || []).length ? (
        <div className="flex flex-wrap gap-1">
          {(c.categories || []).slice(0, 4).map((cat) => (
            <span key={cat} className="px-1.5 py-0.5 rounded-full text-[10px] bg-[#FF451A]/10 text-[#FF451A] font-medium uppercase tracking-wider">{cat}</span>
          ))}
        </div>
      ) : '—',
    },
    {
      label: 'Location',
      render: (c) => (c.cities || []).length ? (
        <span className="inline-flex items-center gap-1"><MapPin size={10} className="text-[#6B6B6B] dark:text-zinc-500" /> {(c.cities || []).slice(0, 2).join(', ')}</span>
      ) : '—',
    },
    {
      label: 'Languages',
      render: (c) => (c.languages || []).length ? (
        <div className="flex flex-wrap gap-1">
          {(c.languages || []).slice(0, 4).map((l) => (
            <span key={l} className="px-1.5 py-0.5 rounded-full text-[10px] bg-white/5 text-zinc-300 border border-black/[0.10] dark:border-white/10 font-medium">{l}</span>
          ))}
        </div>
      ) : '—',
    },
  ];

  // Winner key → readable label for the header crown tooltip
  const winnerLabels = {
    followers: 'Top followers',
    engagement: 'Best engagement',
    reelPrice: 'Lowest reel price',
    brandSafety: 'Best brand safety',
  };

  return (
    <div className="flex min-h-screen">
      <Sidebar />
      <div className="flex-1 p-8 bg-white dark:bg-[#0A0B0D]" data-testid="brand-shortlist-compare-page">
        <div className="max-w-7xl mx-auto">
          <button
            onClick={() => navigate(`/brand/shortlists/${id}`)}
            className="text-sm text-[#6B6B6B] dark:text-zinc-400 hover:text-white flex items-center gap-1 mb-6"
            data-testid="compare-back-button"
          >
            <ArrowLeft size={14} /> Back to {shortlist.name}
          </button>

          <div className="flex items-end justify-between flex-wrap gap-4 mb-2">
            <div>
              <h1 className="text-4xl font-bold tracking-tight text-[#0F0F0F] dark:text-white" data-testid="compare-title">
                Compare creators
              </h1>
              <p className="text-[#6B6B6B] dark:text-zinc-400 mt-1">
                <span className="text-[#0F0F0F] dark:text-white font-semibold" data-testid="compare-count">{creators.length}</span> creators side-by-side
                <span className="mx-2 text-[#A1A1AA] dark:text-zinc-600">•</span>
                <span className="text-[#6B6B6B] dark:text-zinc-500">from "{shortlist.name}"</span>
              </p>
            </div>
            <div className="text-[10px] uppercase tracking-wider text-[#6B6B6B] dark:text-zinc-500 font-mono flex items-center gap-1.5">
              <Crown size={11} className="text-[#FFB800]" /> Best value per metric highlighted
            </div>
          </div>

          {/* Horizontal scroll wrapper for mobile.
              On mobile the column widths stay constant so the user simply swipes. */}
          <div className="mt-6 overflow-x-auto bg-white dark:bg-[#0A0B0D] border border-black/[0.08] dark:border-white/5 rounded-2xl" data-testid="compare-table-wrapper">
            <table className="w-full min-w-[640px] border-collapse" data-testid="compare-table">
              <thead>
                <tr className="border-b border-black/[0.08] dark:border-white/5">
                  <th className="sticky left-0 z-10 bg-white dark:bg-[#0A0B0D] text-left px-4 py-4 text-[10px] uppercase tracking-wider text-[#6B6B6B] dark:text-zinc-500 font-mono w-44 min-w-[176px]">
                    Metric
                  </th>
                  {creators.map((c) => (
                    <th
                      key={c.id}
                      className="text-left px-4 py-4 align-bottom min-w-[200px]"
                      data-testid="compare-column-header"
                    >
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-[#FF451A] to-[#0055FF] flex items-center justify-center text-[#0F0F0F] dark:text-white text-sm font-bold shrink-0">
                          {c.name?.[0]?.toUpperCase() || 'C'}
                        </div>
                        <div className="min-w-0">
                          <Link
                            to={`/brand/creators/${c.id}`}
                            className="text-sm font-semibold text-[#0F0F0F] dark:text-white hover:text-[#FF451A] transition-colors block truncate"
                            data-testid="compare-creator-name"
                          >
                            {c.name}
                          </Link>
                          {c.instagram_handle && (
                            <a
                              href={`https://instagram.com/${c.instagram_handle.replace(/^@/, '')}`}
                              target="_blank"
                              rel="noreferrer"
                              className="text-[11px] text-[#6B6B6B] dark:text-zinc-400 hover:text-[#E1306C] inline-flex items-center gap-0.5 transition-colors"
                            >
                              @{c.instagram_handle} <ExternalLink size={9} />
                            </a>
                          )}
                        </div>
                      </div>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => {
                  const winIdx = row.winnerKey ? winners[row.winnerKey] : -1;
                  return (
                    <tr
                      key={row.label}
                      className="border-b border-black/[0.08] dark:border-white/5 last:border-b-0 hover:bg-white/[0.02] transition-colors"
                      data-testid={`compare-row-${row.label.toLowerCase().replace(/\s+/g, '-')}`}
                    >
                      <td className="sticky left-0 z-10 bg-white dark:bg-[#0A0B0D] px-4 py-3 text-xs text-[#6B6B6B] dark:text-zinc-400 font-medium w-44 min-w-[176px]">
                        <div className="flex items-center gap-1.5">
                          {row.label}
                          {row.winnerKey && (
                            <span className="text-[9px] uppercase tracking-wider text-[#A1A1AA] dark:text-zinc-600 font-mono">
                              {row.winnerKey === 'reelPrice' ? '↓ low wins' : '↑ high wins'}
                            </span>
                          )}
                        </div>
                      </td>
                      {creators.map((c, idx) => {
                        const isWinner = idx === winIdx;
                        return (
                          <td
                            key={c.id}
                            className={`px-4 py-3 text-sm align-middle min-w-[200px] transition-colors ${
                              isWinner
                                ? 'bg-[#00C972]/10 text-[#0F0F0F] dark:text-white font-semibold'
                                : 'text-zinc-300'
                            }`}
                            data-testid={isWinner ? `compare-winner-${row.winnerKey}` : undefined}
                          >
                            <div className="flex items-center gap-1.5">
                              {isWinner && (
                                <Crown
                                  size={12}
                                  className="text-[#00C972] shrink-0"
                                  data-testid={`compare-winner-crown-${row.winnerKey}`}
                                />
                              )}
                              <span className="truncate">{row.render(c)}</span>
                            </div>
                          </td>
                        );
                      })}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Summary footer — quick at-a-glance wins per creator */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3 mt-6" data-testid="compare-wins-summary">
            {creators.map((c, idx) => {
              const wins = Object.entries(winners)
                .filter(([, i]) => i === idx)
                .map(([k]) => winnerLabels[k]);
              return (
                <div
                  key={c.id}
                  className={`rounded-xl border p-4 ${
                    wins.length > 0 ? 'border-[#00C972]/30 bg-[#00C972]/[0.04]' : 'border-black/[0.08] dark:border-white/5 bg-white dark:bg-[#0A0B0D]'
                  }`}
                  data-testid="compare-wins-card"
                >
                  <div className="text-sm font-semibold text-[#0F0F0F] dark:text-white truncate">{c.name}</div>
                  <div className="text-[10px] uppercase tracking-wider text-[#6B6B6B] dark:text-zinc-500 mb-2 font-mono">
                    {wins.length} win{wins.length === 1 ? '' : 's'}
                  </div>
                  {wins.length === 0 ? (
                    <div className="text-xs text-[#6B6B6B] dark:text-zinc-500">No category leader</div>
                  ) : (
                    <div className="space-y-1">
                      {wins.map((w) => (
                        <div key={w} className="text-xs text-[#00C972] inline-flex items-center gap-1 mr-2">
                          <Crown size={10} /> {w}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          <div className="mt-6 flex justify-end">
            <Button
              onClick={() => navigate(`/brand/shortlists/${id}`)}
              className="border border-black/[0.10] dark:border-white/10 bg-white/5 text-[#0F0F0F] dark:text-white hover:bg-white/10 rounded-full px-5 py-2 text-sm"
              data-testid="compare-done-button"
            >
              Done
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};
